import React, { useContext, useEffect, useState } from "react";
import {
    getActiveModule,
    showAppliedFilters,
    titleCase,
} from "@ts/utils";
import AssetUploadComponent from "./AssetUploadComponent";
import { mainContext } from "@ts/constants";
import {
    requestAssetHandler,
    submitUploadedFile,
    downloadRequestedAsset,
} from "@ts/bulk-taxonomy";
import { useDispatch, useSelector } from "react-redux";
import { updateIsExcelUpload } from "@features/isExcelUploadSlice";
import {
    updateBulkTaxonomyDisplay,
    updateIsAllAdgroupsSelected,
    updateIsAllCampaignsSelected,
    updateIsAllPortfoliosSelected,
    updateIsAllPfmDataSelected,
} from "@features/modalSlice";
import UploadedAssetComponent from "./UploadedAssetComponent";
import LinearLoader from "@loaders/LinearLoader";
import BackdropLoader from "@loaders/BackdropLoader";
import {
    updateAllPortfolioId,
    updateAllSysAdgroupId,
    updateAllSysCampaignId,
} from "@features/selectAllGridDataSlice";
import {
    IValueBoolean,
    IMainContext,
    IUploadExcelFile,
    ICommonRowDataRedux,
    ParentModule,
    TCommonRowData,
    IPfmSkuFilterParams,
} from "@ts/common-types";
import { Drawer } from "antd";
import { keys } from "underscore";
import { showToast } from "@/ts/modals";

type TBulkTaxonomyProps = {
    showBulkTaxonomy: boolean;
};

interface ISelectedId {
    allPortfolioId?: ICommonRowDataRedux;
    allSysCampaignId?: ICommonRowDataRedux;
    allSysAdgroupId?: ICommonRowDataRedux;
    isAllPfmDataSelected?: ICommonRowDataRedux;
}

interface ISelectedStatus {
    isAllPortfoliosSelected?: IValueBoolean;
    isAllCampaignsSelected?: IValueBoolean;
    isAllAdgroupsSelected?: IValueBoolean;
}

const BulkTaxonomyModalComponent = React.memo(
    ({ showBulkTaxonomy }: TBulkTaxonomyProps) => {
        const dispatch = useDispatch();
        const {
            activeCheckedNode,
            productMappingGridApi,
            productMappingGridParams
        } = useContext<IMainContext>(mainContext);
        const isAllPortfoliosSelected = useSelector(
            (state: ISelectedStatus) => state.isAllPortfoliosSelected.value
        );
        const isAllCampaignsSelected = useSelector(
            (state: ISelectedStatus) => state.isAllCampaignsSelected.value
        );
        const isAllAdgroupsSelected = useSelector(
            (state: ISelectedStatus) => state.isAllAdgroupsSelected.value
        );
        const allPortfolioId = useSelector(
            (state: ISelectedId) => state.allPortfolioId.value
        );
        const allSysCampaignId = useSelector(
            (state: ISelectedId) => state.allSysCampaignId.value
        );
        const allSysAdgroupId = useSelector(
            (state: ISelectedId) => state.allSysAdgroupId.value
        );
        const isAllPfmDataSelected = useSelector(
            (state: ISelectedId) => state.isAllPfmDataSelected.value
        );
        isAllPfmDataSelected;
        const [uploadExcelFile, setUploadExcelFile] =
            useState<IUploadExcelFile>();
        const [assetFileName, setAssetFileName] = useState<string>();
        const [responseBuffer, setResponseBuffer] = useState<Buffer>();
        const [show, setShow] = useState<boolean>(false);
        const [backdropDisplay, setBackdropDisplay] = useState<boolean>(false);
        const { activeModule } = getActiveModule();
        const closeBulkTaxonomyModal = () => {
            dispatch(updateBulkTaxonomyDisplay(false));
            dispatch(updateIsExcelUpload(false));
            dispatch(updateAllPortfolioId([]));
            dispatch(updateAllSysCampaignId([]));
            dispatch(updateAllSysAdgroupId([]));
            dispatch(updateIsAllPortfoliosSelected(false));
            dispatch(updateIsAllCampaignsSelected(false));
            dispatch(updateIsAllAdgroupsSelected(false));
            dispatch(updateIsAllPfmDataSelected(false));
            setUploadExcelFile(undefined);
            setAssetFileName(undefined);
            setResponseBuffer(undefined);
            setShow(false);
            setBackdropDisplay(false);
        };

        const getSelectedDataLength = () => {
            return isAllPortfoliosSelected
                ? allPortfolioId.length
                : isAllCampaignsSelected
                ? allSysCampaignId.length
                : isAllAdgroupsSelected
                ? allSysAdgroupId.length
                : activeCheckedNode.length;
        };

        const selectedDataLength = getSelectedDataLength();

        const productMappingSelectionData = useSelector(
            (state: { productMappingSelectionData: { value: TCommonRowData } }) =>
                state.productMappingSelectionData.value
        );

        const productMappingFilterModel = useSelector(
            (state: {
                productMappingFilterModel: { value: IPfmSkuFilterParams };
            }) => state.productMappingFilterModel.value
        );

        const getAsset = () => {
            setShow(true);
            requestAssetHandler({
                activeCheckedNode,
                setAssetFileName,
                setResponseBuffer,
                setBackdropDisplay,
                ...(activeModule === ParentModule.SETTINGS && productMappingGridParams ? { productMappingGridParams } : {})
            });
        }

        const handleRequestAsset = () => {
            if (activeModule === ParentModule.SETTINGS) {
                const MAX_EXPORT_ROWS = 100000;
                const EXPORT_LIMIT_MESSAGE = "You can only export up to 100,000 rows at a time, add filters to reduce the number of rows";
                const hasSelectedData = productMappingSelectionData?.length > 0;
                const hasFilters = keys(productMappingFilterModel).length > 0;
                const totalRows = productMappingGridApi?.getDisplayedRowCount() || 0;
                
                if (hasSelectedData) {
                    if (productMappingSelectionData.length <= MAX_EXPORT_ROWS) {
                        getAsset();
                        return;
                    }
                    showToast("danger", EXPORT_LIMIT_MESSAGE);
                    return;
                }

                if (hasFilters) {
                    if (totalRows <= MAX_EXPORT_ROWS) {
                        getAsset();
                        return;
                    }
                }

                showToast("danger", EXPORT_LIMIT_MESSAGE);
            } else {
                getAsset();
            }
        };

        useEffect(() => {
            const timer = setTimeout(() => {
                const element = document.querySelector(
                    "#product-mapping-filters"
                );
                if (element) {
                    showAppliedFilters(
                        productMappingGridApi,
                        productMappingGridParams.filterModel,
                        "#product-mapping-filters"
                    );
                }
            }, 100);

            return () => clearTimeout(timer);
        }, [productMappingGridParams]);

        return (
            <>
                <Drawer
                    title={activeModule === ParentModule.SETTINGS ? "Bulk Mapping" : `${titleCase(activeModule)} Bulk Taxonomy`}
                    placement="right"
                    width={500}
                    onClose={closeBulkTaxonomyModal}
                    open={showBulkTaxonomy}
                >
                    <div
                        className={
                            activeModule === ParentModule.SETTINGS && selectedDataLength
                                ? "d-none"
                                : "clearfix px-3"
                        }
                        id="product-mapping-filters"
                    ></div>
                    <div id="bulk-taxonomy-container">
                        <div
                            className={`total-values-container d-${
                                activeModule === ParentModule.SETTINGS
                                    ? selectedDataLength
                                        ? "block"
                                        : "none"
                                    : "block"
                            }`}
                        >
                            <div>
                                <b>{`Total number of ${activeModule} selected :`}</b>
                                <span className="mx-1 rounded-pill badge bg-dark">
                                    {activeCheckedNode ? selectedDataLength : 0}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div className="request-container process-cards">
                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => handleRequestAsset()}
                                    disabled={
                                        !(
                                            responseBuffer === undefined &&
                                            (activeModule === ParentModule.SETTINGS || selectedDataLength > 0)
                                        ) || show
                                    }
                                >
                                    Request Asset
                                </button>
                                {assetFileName === undefined ? (
                                    <>
                                        <span
                                            className="text-secondary mx-3"
                                            style={{
                                                display: show
                                                    ? "none"
                                                    : "unset",
                                            }}
                                        >
                                            {" "}
                                            No file available to download
                                        </span>
                                        <LinearLoader show={show} />
                                    </>
                                ) : (
                                    <div
                                        className="text-center mx-2 anchor-class"
                                        style={{
                                            fontSize: "12px",
                                        }}
                                        onClick={() =>
                                            downloadRequestedAsset({
                                                responseBuffer,
                                                setAssetFileName,
                                            })
                                        }
                                    >
                                        {assetFileName}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div id="asset-upload-container">
                            {uploadExcelFile === undefined ? (
                                <AssetUploadComponent
                                    {...{ setUploadExcelFile }}
                                />
                            ) : (
                                <UploadedAssetComponent
                                    {...{ setUploadExcelFile }}
                                />
                            )}
                        </div>

                        <div id="bulk-taxonomy-submit">
                            <button
                                className="btn btn-warning mx-2"
                                onClick={closeBulkTaxonomyModal}
                            >
                                Close
                            </button>
                            <button
                                className="btn btn-success my-5"
                                onClick={(e) => {
                                    setBackdropDisplay(true);
                                    submitUploadedFile(
                                        e,
                                        uploadExcelFile,
                                        setBackdropDisplay,
                                        activeModule === ParentModule.SETTINGS ? productMappingGridApi : undefined
                                    );
                                }}
                                disabled={uploadExcelFile === undefined}
                            >
                                Submit
                            </button>
                        </div>
                        <BackdropLoader show={backdropDisplay} />
                    </div>
                </Drawer>
            </>
        );
    }
);

export default BulkTaxonomyModalComponent;